import React from 'react';
import b_ from 'b_';
import get from 'lodash/get';
import set from 'lodash/set';
import cloneDeep from 'lodash/cloneDeep';
import truncate from 'lodash/truncate';

import ModerationDeny from 'client/common/components/moderation-deny';
import {Button, Icon, Textinput} from 'client/common/components/lego';
import {getVideoLink} from 'client/common/utils/video';
import I18N from 'assets/i18n/brandlift-project';

import {CreativeContext, CreativeBox, BordomatikBox, Survey, SurveySlug} from 'client/common/types';

import {useCancelSurveyModeration, useSurvey} from 'client/common/hooks/survey';
import {useSurveySlug} from 'client/common/contexts/slug';
import {useCanEditRevision} from 'client/common/hooks/edit-mode';
import {useAudienceRequest} from 'client/common/hooks/audience';
import {useCreative, UseCreativeParams} from 'client/creatives/hooks/creatives';
import {useResource} from 'client/common/hooks/resources';
import {useIsCreativeAssessor, useIsSuperuser} from 'client/common/hooks/user';
import {useModal} from 'client/common/hooks/modal';
import {usePaymentStatus} from 'client/common/hooks/payment';

import {SurveyState} from 'client/common/store/reducers/survey/types';

import LinkIcon from './icons/link.svg';

import {VideoLinkProps, OptionListsProps} from './types';

import './creative-settings-form.css';

const i18n = I18N.creatives;
const b = b_.with('creative-settings-form');

export const INDUSTRY_OPTIONS = [
    {value: 1, content: i18n.industry['1']},
    {value: 10, content: i18n.industry['10']},
    {value: 4, content: i18n.industry['4']},
    {value: 6, content: i18n.industry['6']},
    {value: 7, content: i18n.industry['7']},
    {value: 2, content: i18n.industry['2']},
    {value: 13, content: i18n.industry['13']},
    {value: 3, content: i18n.industry['3']},
    {value: 8, content: i18n.industry['8']},
    {value: 9, content: i18n.industry['9']},
    {value: 18, content: i18n.industry['18']},
    {value: 12, content: i18n.industry['12']},
    {value: 14, content: i18n.industry['14']},
    {value: 15, content: i18n.industry['15']},
    {value: 16, content: i18n.industry['16']},
    {value: 17, content: i18n.industry['17']},
    {value: 19, content: i18n.industry['19']},
    {value: 20, content: i18n.industry['20']},
    {value: 21, content: i18n.industry['21']},
    {value: 22, content: i18n.industry['22']},
    {value: 23, content: i18n.industry['23']}
];

// обрезаем чтобы поместился попап селект в панель настроек креатива
export const INDUSTRY_SELECT_OPTIONS = INDUSTRY_OPTIONS.map(item => {
    item.content = truncate(item.content, {length: 30}) as any;

    return item;
});

export const ALLOWED_PARAMETERS = {
    creative: [
        'novelty',
        'entertainment',
        'usefulness',
        'desireToTry',
        'makesLifeEasier',
        'savesTime',
        'savesMoney',
        'intentToBuy',
        'heroes',
        'story',
        'video',
        'sound',
        'balance'
    ],
    bordomatik: [
        'novelty',
        'entertainment',
        'usefulness',
        'desireToTry',
        'makesLifeEasier',
        'savesTime',
        'savesMoney',
        'intentToBuy',
        'heroes',
        'balance',
        'helpsBecomeWhoIWantQuestionId',

        'storyUnderstood',
        'storyBelievable',
        'storyCouldHappen',
        'storyToldInMyCommunicationStyle',
        'storyAboutMyProblems',
        'storyMotivates',
        'storyProvedBrandMatchesMyNeeds'
    ]
} as const;

export function VideoLink({id, service, title}: VideoLinkProps) {
    const link = getVideoLink(service, id);

    if (!link) {
        return null;
    }

    return (
        <a
            href={getVideoLink(service, id)}
            className={b('video-link')}
            target='_blank'
            rel='noopener noreferrer'
        >
            <img src={LinkIcon} className={b('video-link-logo')} />
            <span className={b('video-link-text')}>{title}</span>
        </a>
    );
}

export const OptionLists = ({
    errors,
    fields,
    fieldName,
    onBlur,
    disabled,
    rules,
    register,
    placeholderPrefix = i18n.creativeSettings.placeholderPrefix.default,
    onAppend,
    onRemove,
    addItemBtnText = i18n.creativeSettings.addBtnText.default,
    removeItemBtnTitle = i18n.creativeSettings.removeBtnTitle.default,
    minItems = 1,
    maxItems = 3,
    hasNoOpinion = false,
    children
}: OptionListsProps) => {
    return (
        <div className={b('option-list')}>
            {fields.map((field: any, index: number) => {
                const name = `${fieldName}[${index}].text`;
                const isError = get(errors, name);

                if (field.disabled) {
                    return (
                        <div key={field.id}>
                            <div className={b('option-list-row')}>
                                <Textinput
                                    size='m'
                                    name={name}
                                    view='dl'
                                    value={field.text || ''}
                                    disabled
                                    className={b('row')}
                                />
                            </div>
                        </div>
                    );
                }

                return (
                    <div key={field.id}>
                        <div className={b('option-list-row')}>
                            <Textinput
                                size='m'
                                controlRef={register?.(rules)}
                                name={name}
                                view='dl'
                                defaultValue={field.text || ''}
                                placeholder={`${placeholderPrefix} ${String.fromCodePoint(
                                    65 + index
                                )}`}
                                disabled={disabled}
                                className={b('row')}
                                state={isError && 'error'}
                                onBlur={onBlur?.(index)}
                            />
                            {!disabled && fields.length > minItems && onRemove && (
                                <Button
                                    className={b('option-list-remove-btn')}
                                    size='m'
                                    view='default'
                                    onClick={() => onRemove(index)}
                                    title={removeItemBtnTitle}
                                    iconLeft={(cls: string) => (
                                        <Icon className={cls} type='trash' />
                                    )}
                                />
                            )}
                        </div>
                        {isError && (
                            <span className={b('option-list-error')}>
                                {get(errors, `${fieldName}.${index}.text.message`)}
                            </span>
                        )}
                    </div>
                );
            })}
            {hasNoOpinion && (
                <Textinput
                    size='m'
                    view='dl'
                    defaultValue={i18n.creativeSettings.noOpinionAnswer}
                    className={b('row')}
                    disabled
                />
            )}
            {children}
            {!disabled && fields.length < maxItems && (
                <Button
                    className={b('option-list-add-btn')}
                    view='dl-default'
                    size='m'
                    onClick={onAppend}
                    iconLeft={(className: string) => <Icon className={className} type='plus' />}
                >
                    {addItemBtnText}
                </Button>
            )}
        </div>
    );
};

export const setImmut = (obj: Record<string, any>, path: string, value: unknown) =>
    set(cloneDeep(obj), path, value);

// eslint-disable-next-line max-statements
export function useCreativeSettingsProps() {
    const slug = useSurveySlug();
    const survey = useSurvey(slug);
    const canEdit = useCanEditRevision();
    const audience = useAudienceRequest(slug);
    const paymentStatus = usePaymentStatus(slug);

    const state = survey.data?.state;
    const context = survey.data?.box?.context as CreativeContext;

    const resourceId =
        context?.creative?.service === 'resource' ? context?.creative?.id : undefined;
    const resource = useResource(resourceId);

    const creativeParams: UseCreativeParams = {
        slug,
        state,
        audience: audience.data,
        box: survey?.data?.box as BordomatikBox | CreativeBox | undefined
    };

    if (resource.data && 'id' in resource.data) {
        creativeParams.resources = [resource.data];
    }

    const {stat, creative, additionalParameters, brand, industry, targeting} = useCreative(
        creativeParams
    );

    return {
        slug,
        survey,
        canEdit,
        stat,
        creative,
        additionalParameters,
        brand,
        industry,
        targeting,
        paymentStatus
    };
}

export function useModerationCancel(slug: SurveySlug) {
    const isSuperuser = useIsSuperuser();
    const isCreativeAssessor = useIsCreativeAssessor();

    const [showModal, hideModal] = useModal();

    const cancelSurveyModeration = useCancelSurveyModeration(slug);

    return isSuperuser || isCreativeAssessor
        ? () => showModal(<ModerationDeny slug={slug} onDeny={hideModal} />, {withCross: true})
        : cancelSurveyModeration;
}

const COPY_MENU = {
    value: 'copy',
    content: i18n.creativeSettings.menuBtns.copy
};

const CANCEL_MENU = {
    value: 'cancelModeration',
    content: i18n.creativeSettings.menuBtns.cancelModeration
};

export function getMenuItems(state: Survey['state']) {
    if (state === SurveyState.Moderation) {
        return [COPY_MENU, CANCEL_MENU];
    }

    return [COPY_MENU];
}

export const wrapArray = (arr: string[]) => arr.map((text: string) => ({text}));
